<template>
  <div
    class="ProductDefaultLayout
           desk:grid grid-cols-[80px_minmax(0,_1fr)_repeat(10,_minmax(0,_1fr))] gap-x-12
           desk:mb-80 desk:pt-16"
  >
    <!-- spalt 1 -->
    <div class="hidden desk:block col-span-1">
      <div id="ProductImageThumbnails" class=" relative h-full max-h-[468px]" />
    </div>

    <!-- spalt 2 -->
    <div class="mobOnly:w-full mobOnly:mb-24 desk:col-span-6 text-left">
      <ProductImage
        :key="`productImage${activeVariant.partNo}`"
        :product-item="productItem"
        :current-image-list="currentImageList"
        :active-variant="activeVariant"
      />
    </div>

    <!-- spalt 3 -->
    <div class="mobOnly:w-full desk:col-span-5 desk:pl-28">
      <div class="mobOnly:px-12 ">
        <ProductBreadcrumbs v-if="uiStore.isMobile" :product-item="productItem" />

        <!-- * * * * * * name & category -->
        <NameAndCategory
          :product-item="productItem"
          :active-variant="activeVariant"
          :show-description="true"
          :show-collapsed-description="true"
        />
        <!-- * * * * * * structure article -->
        <div v-if="productItem.isStructureArticle && activeVariant.structureArticleProducts?.length" class="my-12">
          <p class="type-headline-sm">{{ $t('productPage.bundles.contains') }}</p>
          <component
            :is="activeVariant.structureArticleProducts.length > 6 ? ReadMore : 'div'"
            :max-height="300"
            :as-button="true"
            :closed-text="$t('productPage.bundles.showAll', {num: activeVariant.structureArticleProducts.length})"
            :open-text="$t('productPage.bundles.showLess')"
            :class="{
              'mb-24': activeVariant.structureArticleProducts.length > 6
            }"
          >
            <div class="flex flex-col">
              <component
                :is="product.url ? NuxtLink : 'div'"
                v-for="product in activeVariant.structureArticleProducts"
                :key="product.partNo"
                :to="product.url"
                class="py-6 flex items-center w-full"
              >
                <div class="w-32 h-40 desk:w-40 desk:h-48 mr-12 relative flex items-center justify-center">
                  <nuxt-img
                    provider="norce"
                    sizes="80px"
                    :src="product.image"
                    class="object-contain w-32 h-32 desk:w-40 desk:h-40"
                    :alt="product.name"
                  />
                  <div class="imageOverlay absolute top-0 left-0 w-full h-full pointer-events-none" />
                </div>
                <div class="flex justify-between flex-1 type-xs desk:type-sm">
                  <div class="w-full">
                    <span class="uppercase">{{ product.quantity }} x {{ product.name }}</span>
                    <div class="text-dark">{{ product.partNo }}</div>
                  </div>
                </div>
              </component>
            </div>
          </component>
        </div>

        <!-- * * * * * * price -->
        <div v-if="!shouldHidePriceResilientProduct && !activeVariant?.isPurchaseDisabled" class="block">
          <ProductPrice
            :key="activeVariant.partNo"
            :product-item="productItem"
            :active-variant="activeVariant"
            design="standard"
          />
        </div>

        <!-- * * * * * * variant selector -->
        <VariantSelector
          :product-item="productItem"
          :active-variant="activeVariant"
          design="standard"
          @update-active-variant="(variant) => updateActiveVariant(variant)"
        />
        <div v-if="!shouldHidePriceAndPurchaseResilientProduct && !hasPageTypeConflict" class="mobOnly:px-12">
          <!-- * * * * * * quantity and buy button -->
          <BuyBox
            v-if="activeVariant"
            :key="activeVariant.partNo"
            :active-variant="activeVariant"
            :product-item="productItem"
            :page-type="pageType"
            design="standard"
          />

          <div v-if="backorderNotices.length > 0 && backorderNoticeTotalQuantity" class="bg-attentionPrimary p-16 type-sm-medium mobOnly:mb-12">
            <span v-if="backorderNotices[0].estimatedDeliveryDate">
              {{ $t('product.backorderNoticeWithDate', {quantity: backorderNoticeTotalQuantity, date: backorderNotices[0].estimatedDeliveryDate}) }}
            </span>
            <span v-else>
              {{ $t('product.backorderNotice', {quantity: backorderNoticeTotalQuantity}) }}
            </span>
          </div>
        </div>

        <div v-else>
          <!-- Show stock status for resilient products when logged in on US market -->
          <div v-if="shouldShowResilientStockStatus" class="mb-16">
            <StockStatusSwitcher
              :total-quantity="1"
              :product-item="productItem"
              :active-variant="activeVariant"
            />
          </div>

          <div
            class="bg-bg-warning flex p-16 text-left type-xs-medium mb-16"
          >
            <img
              class="inline-block w-16 h-16 desk:w-20 desk:h-20 mr-12"
              src="/icons/info.svg"
              alt=""
            >
            <div>{{ $t('resilient.contactSalesInfo') }}</div>
          </div>
          <NuxtLink
            v-if="globalContent.getContactUsPage"
            :to="$sbLinkFix(globalContent.getContactUsPage)"
            class="btn btn--lg w-full"
          >
            {{ $t('resilient.contactSalesButton') }}
          </NuxtLink>
        </div>

        <div class="desk:hidden px-12 mb-32">

          <SafetyInformation
            :active-variant="activeVariant"
            :product-item="productItem"
            design="b2b"
          />

          <!-- * * * * * * mobile details -->
          <GenericAccordion :headline="$t('productPage.productFacts')">
            <ShowParametrics
              :active-variant="activeVariant"
              :product-item="productItem"
              class="mb-32"
            />
          </GenericAccordion>

          <ProductFiles
            :active-variant="activeVariant"
            :product-item="productItem"
          />

          <ProductArticlesAccordion :product-item="productItem" />

          <!-- * * * * mobile bundles -->
          <ProductBundles
            v-if="activeVariant.isIncludedInStructureArticle"
            :active-variant="activeVariant"
            :skip-fetch="!uiStore.isMobile"
          />
        </div>
      </div>
    </div>

    <!-- rad 2 -->
    <div class="col-span-1"/>
    <!-- rad 2 spalt 2 -->
    <div class="col-span-6 desk:pr-32 ">
      <!-- * * * * * * desktop details -->
      <div class="mobOnly:hidden">
        <GenericAccordion :headline="$t('productPage.productFacts')" :start-open="false">
          <ShowParametrics
            :active-variant="activeVariant"
            :product-item="productItem"
            class="mb-32"
          />
        </GenericAccordion>
        <ProductFiles
          :active-variant="activeVariant"
          :product-item="productItem"
        />

        <ProductArticlesAccordion :product-item="productItem" />

        <!-- * * * * desktop bundles -->
        <ProductBundles
          v-if="activeVariant.isIncludedInStructureArticle"
          :active-variant="activeVariant"
          :skip-fetch="uiStore.isMobile"
        />
      </div>

    </div>
    <!-- rad 2 col 3 -->
    <div class="col-span-5">
      <SafetyInformation
        :active-variant="activeVariant"
        :product-item="productItem"
        class="mobOnly:hidden"
        design="b2b"
      />
    </div>

  </div>
</template>

<script lang="ts" setup>
import VariantSelector from '~/components/product-page/VariantSelector.vue';
import BuyBox from '~/components/product-page/BuyBox.vue';
import ProductImage from '~/components/product-page/ProductImage.vue';
import NameAndCategory from '~/components/product-page/NameAndCategory.vue';
import ProductPrice from '~/components/product-page/ProductPrice.vue';
import ProductBundles from '~/components/product-page/ProductBundles.vue';
import StockStatusSwitcher from '~/components/product/StockStatusSwitcher.vue';
import type { ProductModel } from '#models/product';
import type { ProductVariantModel } from '#models/productVariant';
import type { NorceMediaSetCodes } from '#constants/norceCodes';
import { useProductStore } from '~/store/product';
import { useUserStore } from '~/store/user';
import { useGlobalContentStore } from '~/store/globalContent';

import { storeToRefs } from 'pinia';
import ShowParametrics from '~/components/product-page/ShowParametrics.vue';
import GenericAccordion from '~/components/generic/GenericAccordion.vue';
import ProductFiles from '~/components/product-page/ProductFiles.vue';
import ProductBreadcrumbs from '~/components/product-page/ProductBreadcrumbs.vue';

import { useUiStore } from '~/store/ui';
import ProductArticlesAccordion from '~/components/product-page/ProductArticlesAccordion.vue';
import { useCartStore } from '~/store/cart';
import SafetyInformation from '~/components/product-page/SafetyInformation.vue';

const uiStore = useUiStore();

const productStore = useProductStore();
const userStore = useUserStore();
const globalStore = useGlobalContentStore();
const cartStore = useCartStore();

const NuxtLink = resolveComponent('NuxtLink');
const ReadMore = resolveComponent('ReadMore');

const globalContent = useGlobalContentStore();

const emit = defineEmits<{
  (e: 'updateActiveVariant', obj: ProductVariantModel): void;
}>();

const props = defineProps<{
  productItem: ProductModel,
  activeVariant: ProductVariantModel,
  currentImageList: NorceMediaSetCodes,
  pageType?: 'b2b' | 'b2c'
}>();

if (userStore.loggedIn && globalStore.getAllowBackorders) {
  await productStore.fetchBackorderNotice(props.activeVariant.partNo);
}

const backorderNotices = computed(() => {
  return productStore.backorderNotices.filter((i) => i.productNo === props.activeVariant.partNo);
});

const backorderNoticeTotalQuantity = computed(() => {
  return backorderNotices.value.reduce((a, c) => a + c.quantity, 0);
});

/**
 * Hides price/stockstatus
 */
const shouldHidePriceAndPurchaseResilientProduct = computed(()=> {
  const isResilient = props.productItem.categories.some(f => f.code === 'Resilient' || f.code === 'ResilientAccessories');
  return isResilient && globalStore.disableResilientPricingAndPurchase;
});

const shouldHidePriceResilientProduct = computed(()=> {
  const isResilient = props.productItem.categories.some(f => f.code === 'Resilient' || f.code === 'ResilientAccessories');
  return isResilient && globalStore.disableResilientPricingAndPurchase && !globalStore.getEnableResilientPricing;
});

// B2C user viewing B2B page - hide buy functionality
const hasPageTypeConflict = computed(() => props.pageType === 'b2b' && globalContent.getIsB2c);

/* Shows stock status for resilient products when logged in on US market only */
const shouldShowResilientStockStatus = computed(()=> {
  const isResilient = props.productItem.categories.some(f => f.code === 'Resilient' || f.code === 'ResilientAccessories');
  return isResilient && userStore.isLoggedIn && globalContent.getCountryCode === 'us';
});

const fetchBackorderNotice = async() => {
  await productStore.fetchBackorderNotice(props.activeVariant.partNo);
};

const { loggedIn } = storeToRefs(userStore);
watch(loggedIn, async() => {
  if (loggedIn.value && globalStore.getAllowBackorders) {
    await fetchBackorderNotice();
  }
});

const updateActiveVariant = async(variant: ProductVariantModel) => {
  emit('updateActiveVariant', variant);
  if (loggedIn.value && globalStore.getAllowBackorders) {
    await fetchBackorderNotice();
  }
};
</script>
